import React, { useState, useEffect } from 'react';
import { DropDownComponent, InputComponent, FileUploadComponent } from '../custcomponents';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface Testimonial {
    id: string;
    name: string;
    photoUrl?: string;
    content: string;
    date: number;
    eventcode?: string;
    status: string;
}

export const ManageTestimonials = () => {
    const { eventcode } = useParams<{ eventcode: string }>();
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
    const [newTestimonial, setNewTestimonial] = useState({
        name: '',
        content: '',
        eventcode: eventcode || '',
        photoFile: null as File | null,
        photoUrl: '',
        contentType: '',
        status: 'DRAFT'
    });

    useEffect(() => {
        fetchTestimonials();
    }, []);

    useEffect(() => {
        if (editingTestimonial) {
            setNewTestimonial({
                name: editingTestimonial.name,
                content: editingTestimonial.content,
                eventcode: editingTestimonial.eventcode || eventcode || '',
                photoFile: null,
                photoUrl: editingTestimonial.photoUrl || '',
                contentType: '',
                status: editingTestimonial.status || 'DRAFT'
            });
        } else {
            setNewTestimonial({
                name: '',
                content: '',
                eventcode: eventcode || '',
                photoFile: null,
                photoUrl: '',
                contentType: '',
                status: 'DRAFT'
            });
        }
    }, [editingTestimonial, eventcode]);

    const fetchTestimonials = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTestimonials', {
                action: 'getTestimonials',
                data: { eventcode }
            });
            if (response.data.success) {
                const testimonialsObj = response.data.testimonials || {};
                const testimonialsArray = Object.keys(testimonialsObj).map(id => ({
                    id,
                    ...testimonialsObj[id]
                }));
                // Sort by date descending (latest first)
                testimonialsArray.sort((a, b) => b.date - a.date);
                setTestimonials(testimonialsArray);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch testimonials');
        }
        setLoading(false);
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remove the data:image/jpeg;base64, prefix
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleAddTestimonial = async () => {
        if (!newTestimonial.name.trim()) {
            toast.error('Name is required');
            return;
        }
        if (!newTestimonial.content.trim()) {
            toast.error('Content is required');
            return;
        }
        setLoading(true);
        try {
            let data = {
                name: newTestimonial.name,
                content: newTestimonial.content,
                eventcode: newTestimonial.eventcode || null,
                status: newTestimonial.status
            };
            
            if (editingTestimonial) {
                data = { ...data, testimonialId: editingTestimonial.id } as any;
            }
            
            if (newTestimonial.photoFile) {
                const photoBase64 = await fileToBase64(newTestimonial.photoFile);
                data = { ...data, photoBase64, photoFileName: newTestimonial.photoFile.name, contentType: newTestimonial.contentType } as any;
            }
            
            const response = await AxiosAdminInstance.post('/api/admin/manageTestimonials', {
                action: editingTestimonial ? 'updateTestimonial' : 'createTestimonial',
                data
            });
            
            if (response.data.success) {
                toast.success(editingTestimonial ? 'Testimonial updated successfully' : 'Testimonial created successfully');
                setNewTestimonial({
                    name: '',
                    content: '',
                    eventcode: eventcode || '',
                    photoFile: null,
                    photoUrl: '',
                    contentType: '',
                    status: 'DRAFT'
                });
                setEditingTestimonial(null);
                fetchTestimonials();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(editingTestimonial ? 'Failed to update testimonial' : 'Failed to create testimonial');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewTestimonial({
            name: '',
            content: '',
            eventcode: eventcode || '',
            photoFile: null,
            photoUrl: '',
            contentType: '',
            status: 'DRAFT'
        });
        setEditingTestimonial(null);
    };

    const handleDeleteTestimonial = async (testimonialId: string) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.post('/api/admin/manageTestimonials', {
                action: 'deleteTestimonial',
                data: { testimonialId }
            });
            if (response.data.success) {
                toast.success('Testimonial deleted successfully');
                fetchTestimonials();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete testimonial');
        }
        setLoading(false);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return false;
            }
            // Validate file size (100KB max)
            if (file.size > 100 * 1024) {
                toast.error('File size must be less than 100KB');
                return false;
            }
            setNewTestimonial(prev => ({ ...prev, photoFile: file, photoUrl: '', contentType: file.type }));
            return true;
        }
        return false;
    };

    const testimonialColumns = [
        { name: 'Actions', cell: (row: Testimonial) => (
            <div>
                <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingTestimonial(row)} title="Edit">
                    <i className="fa fa-edit"></i>
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => handleDeleteTestimonial(row.id)} title="Delete">
                    <i className="fa fa-trash"></i>
                </button>
            </div>
        ) },
        { name: 'Name', selector: (row: Testimonial) => row.name, sortable: true },
        { name: 'Content', selector: (row: Testimonial) => row.content, sortable: true },
        { name: 'Event Code', selector: (row: Testimonial) => row.eventcode || 'N/A', sortable: true },
        { name: 'Status', selector: (row: Testimonial) => row.status, sortable: true },
        { name: 'Date', selector: (row: Testimonial) => new Date(row.date).toLocaleDateString(), sortable: true },
        { name: 'Photo', cell: (row: Testimonial) => (
            row.photoUrl ? <img src={row.photoUrl} alt={row.name} style={{ width: '50px', height: '50px', objectFit: 'cover' }} /> : 'No Image'
        ) }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-quote-left fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Testimonials</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddTestimonial(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="name"
                                            custplaceholder="Name"
                                            custid="testimonialName"
                                            onExternalValue={(val: string) => setNewTestimonial(prev => ({ ...prev, name: val }))}
                                            onExternalBind={newTestimonial.name}
                                        />
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="eventcode"
                                            custplaceholder="Event Code (optional)"
                                            custid="testimonialEventCode"
                                            onExternalValue={(val: string) => setNewTestimonial(prev => ({ ...prev, eventcode: val }))}
                                            onExternalBind={newTestimonial.eventcode}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="testimonialContent">Content</label>
                                            <textarea
                                                id="testimonialContent"
                                                className="form-control"
                                                rows={4}
                                                placeholder="Testimonial content"
                                                value={newTestimonial.content}
                                                onChange={(e) => setNewTestimonial(prev => ({ ...prev, content: e.target.value }))}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <FileUploadComponent
                                            custname="photo"
                                            custplaceholder="Upload Photo"
                                            custid="testimonialPhoto"
                                            onChange={handleFileChange}
                                        />
                                        {newTestimonial.photoUrl && (
                                            <div className="mt-2">
                                                <img src={newTestimonial.photoUrl} alt="Current" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <div className="form-group">
                                            <label htmlFor="testimonialStatus">Status</label>
                                            <select
                                                id="testimonialStatus"
                                                className="form-control"
                                                value={newTestimonial.status}
                                                onChange={(e) => setNewTestimonial(prev => ({ ...prev, status: e.target.value }))}
                                            >
                                                <option value="DRAFT">Draft</option>
                                                <option value="PUBLISHED">Published</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn btn-primary" type="submit" disabled={loading}>
                                            {editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingTestimonial && (
                                            <button className="btn btn-secondary ms-2" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Testimonials</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={testimonialColumns}
                                            data={testimonials}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};


